import { useState } from "react";
import { ArrowDownWideNarrow, ArrowUpNarrowWide, LayoutGrid, List, PanelRight, X } from "lucide-react";
import { Menu, anchorFrom, type MenuAnchor, type MenuEntry } from "@/components/ui/menu";
import { CATEGORY_STYLE, TYPE_FILTERS, type TypeFilter } from "@/lib/fileTypes";
import { cn } from "@/lib/utils";
import type { DriveVariant, ViewMode } from "../types";
import { defaultDirFor, type SortKey, type SortState } from "./sorting";

/** A Fluent command-bar button (`.cmd` in the mock-up). */
export const cmdClass =
    "flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[4px] px-2 text-foreground transition-colors hover:bg-[var(--drive-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40";

/** A filter chip (`.chip`); `active` = `.chip.on`. */
export function chipClass(active: boolean): string {
    return cn(
        "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[4px] border px-2.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active
            ? "border-primary/45 bg-[var(--drive-primary-soft)] font-semibold text-primary"
            : "border-[var(--drive-line-strong)] bg-card text-foreground hover:bg-[var(--drive-hover)]"
    );
}

/** List / grid segmented toggle. */
export function ViewToggle({ view, onChange }: { view: ViewMode; onChange: (v: ViewMode) => void }) {
    const seg = (mode: ViewMode, label: string, Icon: typeof List) => (
        <button
            type="button"
            aria-pressed={view === mode}
            aria-label={`${label} layout`}
            title={`${label} layout`}
            onClick={() => onChange(mode)}
            className={cn(
                "flex h-7 items-center gap-1.5 px-2.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                view === mode
                    ? "bg-[var(--drive-primary-soft)] font-semibold text-primary"
                    : "text-[var(--drive-text-2)] hover:bg-[var(--drive-hover)] hover:text-foreground"
            )}
        >
            <Icon className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">{label}</span>
        </button>
    );
    return (
        <div role="group" aria-label="Layout" className="flex shrink-0 overflow-hidden rounded-[4px] border border-[var(--drive-line-strong)]">
            {seg("list", "List", List)}
            <div className="w-px bg-[var(--drive-line-strong)]" aria-hidden />
            {seg("grid", "Grid", LayoutGrid)}
        </div>
    );
}

/** Details-pane toggle; `badge` = open comment threads on the selected file. */
export function DetailsToggle({ open, onToggle, badge = 0 }: { open: boolean; onToggle: () => void; badge?: number }) {
    const base = open ? "Hide details" : "View details";
    const label = badge > 0 ? `${base} — ${badge} open ${badge === 1 ? "comment" : "comments"}` : base;
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-pressed={open}
            aria-label={label}
            title={label}
            className={cn(cmdClass, "relative w-8 justify-center px-0", open && "bg-[var(--drive-selected)] text-primary hover:bg-[var(--drive-selected)]")}
        >
            <PanelRight className="h-4 w-4" aria-hidden />
            {badge > 0 ? (
                <span
                    aria-hidden
                    className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground ring-2 ring-card"
                >
                    {badge > 99 ? "99+" : badge}
                </span>
            ) : null}
        </button>
    );
}

/** Client-side type filter chips. Clicking the active chip clears it. */
export function FilterChips({ value, onChange }: { value: TypeFilter | null; onChange: (v: TypeFilter | null) => void }) {
    return (
        <div role="toolbar" aria-label="Filter by type" data-no-clear="" className="drive-no-scrollbar flex items-center gap-1.5 overflow-x-auto pb-2.5 pt-1">
            {TYPE_FILTERS.map((f) => {
                const active = value === f.id;
                const Icon = CATEGORY_STYLE[f.category].icon;
                return (
                    <button key={f.id} type="button" aria-pressed={active} onClick={() => onChange(active ? null : f.id)} className={chipClass(active)}>
                        <Icon className="h-4 w-4" aria-hidden />
                        {f.label}
                        {active ? <X className="h-3.5 w-3.5" aria-hidden /> : null}
                    </button>
                );
            })}
        </div>
    );
}

const SORT_LABEL: Record<SortKey, string> = {
    name: "Name",
    modified: "Modified",
    size: "Size",
    opened: "Last opened",
    trashed: "Date binned",
    owner: "Owner"
};

const SORT_KEYS: Record<DriveVariant, SortKey[]> = {
    folder: ["name", "modified", "size", "owner"],
    starred: ["name", "modified", "size", "owner"],
    search: ["name", "modified", "size", "owner"],
    shared: ["name", "modified", "size", "owner"],
    recent: ["opened", "name", "modified", "size", "owner"],
    trash: ["trashed", "name", "size"]
};

/** Command-bar sort: field + direction (the list's column headers sort too). */
export function SortButton({ variant, sort, onChange }: { variant: DriveVariant; sort: SortState; onChange: (s: SortState) => void }) {
    const [menu, setMenu] = useState<{ anchor: MenuAnchor; keyboard: boolean } | null>(null);
    const Icon = sort.dir === "asc" ? ArrowUpNarrowWide : ArrowDownWideNarrow;
    const label = SORT_LABEL[sort.key];
    const entries: MenuEntry[] = [
        ...SORT_KEYS[variant].map((k) => ({
            id: k,
            label: SORT_LABEL[k],
            checked: sort.key === k,
            onSelect: () => onChange(sort.key === k ? sort : { key: k, dir: defaultDirFor(k) })
        })),
        { kind: "separator", id: "dir" },
        { id: "asc", label: "Ascending", checked: sort.dir === "asc", onSelect: () => onChange({ ...sort, dir: "asc" }) },
        { id: "desc", label: "Descending", checked: sort.dir === "desc", onSelect: () => onChange({ ...sort, dir: "desc" }) }
    ];
    return (
        <>
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={Boolean(menu)}
                aria-label={`Sort by ${label.toLowerCase()}, ${sort.dir === "asc" ? "ascending" : "descending"}`}
                title="Sort"
                onClick={(e) => setMenu({ anchor: anchorFrom(e.currentTarget, "end"), keyboard: e.detail === 0 })}
                className={cmdClass}
            >
                <Icon className="h-4 w-4" aria-hidden />
                <span className="hidden text-[13px] sm:inline">{label}</span>
            </button>
            {menu ? (
                <Menu label="Sort by" anchor={menu.anchor} focusFirst={menu.keyboard} onClose={() => setMenu(null)} entries={entries} className="w-52" />
            ) : null}
        </>
    );
}
